import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUp, Users, DollarSign, Calendar, Mail, ArrowRight, CheckCircle, AlertCircle, Wallet, Send, Award, ChevronDown } from 'lucide-react';
import SEO from '../components/SEO';
import RoadmapGantt from '../components/RoadmapGantt';

const Investors = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);

  useEffect(() => {
    const isAuth = sessionStorage.getItem('investorAuth');
    if (!isAuth) {
      navigate('/investor-login');
    }
  }, [navigate]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/investors/capture-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      if (response.ok) {
        console.log('Email captured:', email, 'to: investors@cauratech.com');
        setEmailSent(true);
        setTimeout(() => {
          setEmailSent(false);
          setEmail('');
        }, 5000);
      } else {
        console.error('Error capturing email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 }
  };

  return (
    <>
      <SEO 
        title="Invierte en KOMPA | Plataforma SNBL Venezuela"
        description="KOMPA está revolucionando las finanzas personales en Venezuela con el modelo SNBL. Únete a nuestra ronda de inversión."
        pathname="/investors"
      />

      <div className="bg-white">
        {/* HERO SECTION */}
        <section className="min-h-screen bg-gradient-to-br from-[#1A1A1A] via-[#2A2A2A] to-[#1A1A1A] text-white relative overflow-hidden flex items-center">
          <motion.div 
            className="absolute top-20 right-20 w-96 h-96 bg-[#F5A623] rounded-full blur-3xl opacity-20"
            animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
          <motion.div 
            className="absolute bottom-20 left-20 w-96 h-96 bg-[#FF9500] rounded-full blur-3xl opacity-20"
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.3, 0.2] }}
            transition={{ duration: 8, repeat: Infinity, delay: 4 }}
          />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mb-12 relative"
              >
                {/* Backdrop Gradient Animado */}
                <motion.div
                  animate={{ 
                    background: [
                      'radial-gradient(circle, rgba(245,166,35,0.3) 0%, rgba(255,149,0,0.2) 50%, transparent 100%)',
                      'radial-gradient(circle, rgba(255,149,0,0.3) 0%, rgba(245,166,35,0.2) 50%, transparent 100%)',
                      'radial-gradient(circle, rgba(245,166,35,0.3) 0%, rgba(255,149,0,0.2) 50%, transparent 100%)'
                    ]
                  }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute inset-0 blur-3xl"
                  style={{ width: '120%', height: '120%', left: '-10%', top: '-10%' }}
                />
                
                {/* Glow Pulsante */}
                <motion.div
                  animate={{ 
                    boxShadow: [
                      '0 0 40px 20px rgba(245,166,35,0.4)',
                      '0 0 80px 40px rgba(255,149,0,0.6)',
                      '0 0 40px 20px rgba(245,166,35,0.4)'
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute inset-0 rounded-full"
                  style={{ filter: 'blur(20px)' }}
                />
                
                {/* Borde Brillante Animado */}
                <motion.div
                  animate={{ 
                    rotate: [0, 360]
                  }}
                  transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0"
                  style={{ 
                    background: 'conic-gradient(from 0deg, transparent 0%, rgba(245,166,35,0.8) 50%, transparent 100%)',
                    borderRadius: '50%',
                    filter: 'blur(8px)',
                    width: '110%',
                    height: '110%',
                    left: '-5%',
                    top: '-5%'
                  }}
                />
                
                {/* Logo con Float */}
                <motion.img
                  src="https://customer-assets.emergentagent.com/job_seo-enhanced-kompa/artifacts/dmk050na_KOMPA%204.png"
                  alt="KOMPA"
                  className="h-48 sm:h-60 lg:h-72 mx-auto relative z-10"
                  style={{ 
                    filter: 'drop-shadow(0 25px 50px rgba(0, 0, 0, 0.5)) drop-shadow(0 10px 30px rgba(245, 166, 35, 0.3))'
                  }}
                  animate={{ 
                    y: [0, -15, 0],
                    scale: [1, 1.02, 1]
                  }}
                  transition={{ 
                    duration: 4, 
                    repeat: Infinity, 
                    ease: "easeInOut" 
                  }}
                />
              </motion.div>
              
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-5xl sm:text-6xl lg:text-7xl font-black mb-6 leading-tight"
              >
                La primera plataforma<br />
                <span className="text-[#F5A623]">SNBL de Venezuela</span>
              </motion.h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="text-xl sm:text-2xl text-gray-300 mb-12 leading-relaxed"
              >
                Construyendo el futuro de las finanzas personales donde el ahorro vence a la deuda.<br />
                <span className="text-[#F5A623] font-bold">+8,000 usuarios</span> ya confían en KOMPA.
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(245, 166, 35, 0.4)" }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-[#F5A623] text-[#1A1A1A] font-black rounded-xl hover:bg-[#FF9500] transition-all duration-300 flex items-center justify-center gap-2 text-lg shadow-xl"
                  onClick={() => window.alert('Funcionalidad de Calendly pendiente de configuración')}
                >
                  Agendar reunión
                  <Calendar size={20} />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => document.getElementById('email-form').scrollIntoView({ behavior: 'smooth' })}
                  className="px-8 py-4 bg-white/10 text-white font-black rounded-xl hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-2 text-lg border border-white/20 backdrop-blur-sm"
                >
                  Contactar por email
                  <Mail size={20} />
                </motion.button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* EL PROBLEMA */}
        <section className="py-20 bg-gradient-to-b from-white to-[#FFF9ED]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...fadeInUp} className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-black text-[#1A1A1A] mb-4">El Problema</h2>
              <p className="text-xl text-gray-600">Por qué ahora, por qué Venezuela</p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: <TrendingUp size={40} />,
                  title: 'Inflación devastadora',
                  description: 'El ahorro pierde valor cada día sin protección'
                },
                {
                  icon: <AlertCircle size={40} />,
                  title: 'Sin herramientas accesibles',
                  description: 'Los venezolanos no tienen opciones simples para planificar metas reales'
                },
                {
                  icon: <DollarSign size={40} />,
                  title: 'Remesas sin rumbo',
                  description: '$4B+ anuales que se consumen sin cumplir objetivos'
                },
                {
                  icon: <Users size={40} />,
                  title: 'Desconfianza institucional',
                  description: 'Demanda de transparencia y trazabilidad'
                }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  {...fadeInUp}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100"
                >
                  <div className="text-[#F5A623] mb-4">{item.icon}</div>
                  <h3 className="text-xl font-black text-[#1A1A1A] mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* LA SOLUCIÓN KOMPA */}
        <section className="py-20 bg-[#FFF9ED]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...fadeInUp} className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-black text-[#1A1A1A] mb-4">La Solución KOMPA</h2>
              <p className="text-xl text-gray-600">El modelo SNBL y nuestros diferenciadores</p>
            </motion.div>

            {/* MANTRA VISUAL */}
            <motion.div {...fadeInUp} className="mb-16">
              <div className="bg-gradient-to-r from-[#F5A623] to-[#FF9500] rounded-3xl p-12 text-center">
                <div className="flex flex-col sm:flex-row justify-center items-center gap-8 text-white">
                  <div className="flex-1">
                    <h3 className="text-3xl font-black mb-2">IMAGÍNALO</h3>
                  </div>
                  <div className="text-4xl font-black">→</div>
                  <div className="flex-1">
                    <h3 className="text-3xl font-black mb-2">KONSTRUYELO</h3>
                  </div>
                  <div className="text-4xl font-black">→</div>
                  <div className="flex-1">
                    <h3 className="text-3xl font-black mb-2">LÓGRALO</h3>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* PILARES */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: <CheckCircle size={40} />,
                  title: 'SNBL (Save Now, Buy Later)',
                  description: 'El usuario define meta, plazo (hasta 36 meses), frecuencia. Ahorra primero, compra después.'
                },
                {
                  icon: <TrendingUp size={40} />,
                  title: 'Indexación',
                  description: 'Protección del valor del ahorro contra devaluación (1% mensual sobre meta).'
                },
                {
                  icon: <Wallet size={40} />,
                  title: 'Wallet con propósito',
                  description: 'Bolsillos por meta, trazabilidad completa, historial auditable.'
                },
                {
                  icon: <Send size={40} />,
                  title: 'Remesas con destino',
                  description: 'Familiares envían directo a una meta, no a un vacío.'
                },
                {
                  icon: <Award size={40} />,
                  title: 'Reintegro por disciplina',
                  description: 'Devolvemos 50% de indexación si completas solo ahorrando.'
                }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  {...fadeInUp}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
                >
                  <div className="text-[#F5A623] mb-4">{item.icon}</div>
                  <h3 className="text-xl font-black text-[#1A1A1A] mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* MODELO DE NEGOCIO */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...fadeInUp} className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-black text-[#1A1A1A] mb-4">Modelo de Negocio</h2>
              <p className="text-xl text-gray-600">Cómo KOMPA genera ingresos y escala</p>
            </motion.div>

            <motion.div {...fadeInUp} className="bg-gradient-to-br from-[#FFF9ED] to-white rounded-3xl p-8 shadow-xl mb-12">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-[#F5A623]">
                      <th className="text-left py-4 px-4 font-black text-[#1A1A1A] text-lg">Fuente</th>
                      <th className="text-left py-4 px-4 font-black text-[#1A1A1A] text-lg">Precio / Fee</th>
                      <th className="text-left py-4 px-4 font-black text-[#1A1A1A] text-lg">Descripción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { source: 'Kompa Club', price: '€1.99 / mes', desc: 'Suscripción premium con beneficios' },
                      { source: 'Indexación', price: '1% mensual sobre meta', desc: 'Protección del valor del ahorro' },
                      { source: 'Wallet/Pagos', price: 'Comisiones', desc: 'Por transacciones y servicios' },
                      { source: 'Remesas', price: 'Fee por envío', desc: 'Canalizar remesas a metas' },
                      { source: 'Aliados comerciales', price: 'Comisión por cierre', desc: 'Cuando se ejecuta la meta con aliado' },
                      { source: 'Impulso final (futuro)', price: 'Revenue share', desc: 'Crédito puente con bancos aliados' }
                    ].map((row, idx) => (
                      <tr key={idx} className="border-b border-gray-200 hover:bg-[#FFF9ED] transition-colors">
                        <td className="py-4 px-4 font-bold text-[#1A1A1A]">{row.source}</td>
                        <td className="py-4 px-4 text-[#F5A623] font-bold">{row.price}</td>
                        <td className="py-4 px-4 text-gray-600">{row.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>

            <motion.div {...fadeInUp} className="bg-gradient-to-r from-[#F5A623] to-[#FF9500] rounded-2xl p-8 text-white text-center">
              <p className="text-xl mb-2">Ejemplo práctico:</p>
              <p className="text-2xl font-black">Meta de €300 a 12 meses → Indexación €36 total → Si completa: reintegro €18</p>
            </motion.div>
          </div>
        </section>

        {/* TRACCIÓN */}
        <section className="py-20 bg-gradient-to-b from-[#FFF9ED] to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...fadeInUp} className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-black text-[#1A1A1A] mb-4">Tracción</h2>
              <p className="text-xl text-gray-600">Validación real del mercado</p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { number: '+8,000', label: 'Usuarios registrados' },
                { number: '100%', label: 'Modelo híbrido probado' },
                { number: 'Múltiples', label: 'Alianzas estratégicas' },
                { number: 'Lista', label: 'Base tecnológica para escalar' }
              ].map((stat, idx) => (
                <motion.div
                  key={idx}
                  {...fadeInUp}
                  transition={{ delay: idx * 0.1 }}
                  className="text-center p-8 bg-white rounded-2xl shadow-lg"
                >
                  <div className="text-5xl font-black text-[#F5A623] mb-4">{stat.number}</div>
                  <p className="text-lg font-bold text-[#1A1A1A]">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ROADMAP */}
        <section className="py-20 bg-gradient-to-br from-[#1A1A1A] via-[#2A2A2A] to-[#1A1A1A]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...fadeInUp} className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-black text-white mb-4">Roadmap 18 Meses</h2>
              <p className="text-xl text-gray-300">Visión clara de ejecución - 78 semanas, 62 tareas, 8 fases</p>
              <p className="text-sm text-gray-400 mt-2">Gantt interactivo con dependencias entre tareas</p>
            </motion.div>

            <motion.div {...fadeInUp}>
              <RoadmapGantt />
            </motion.div>
          </div>
        </section>

        {/* OPORTUNIDAD DE INVERSIÓN */}
        <section className="py-20 bg-gradient-to-br from-[#1A1A1A] via-[#2A2A2A] to-[#1A1A1A] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...fadeInUp} className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-black mb-4">La Oportunidad de Inversión</h2>
              <p className="text-xl text-gray-300">El ask concreto y uso de fondos</p>
            </motion.div>

            <motion.div {...fadeInUp} className="text-center mb-16">
              <div className="inline-block bg-[#F5A623] rounded-3xl p-12">
                <p className="text-2xl text-[#1A1A1A] font-bold mb-4">Ronda semilla</p>
                <h3 className="text-7xl font-black text-[#1A1A1A] mb-4">$2M USD</h3>
                <p className="text-xl text-[#1A1A1A] font-bold">18 meses de runway</p>
              </div>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {[
                { percentage: '45%', title: 'Tecnología', desc: 'Motor SNBL, wallet, indexación, Kompa Club, IA, analítica' },
                { percentage: '25%', title: 'Growth', desc: 'Adquisición, marketing, aliados, expansión comercial' },
                { percentage: '20%', title: 'Reserva', desc: 'Contingencia/operación y respaldo estructural' },
                { percentage: '10%', title: 'Legal & Compliance', desc: 'Regulación, contratos, auditorías, gobierno corporativo' }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  {...fadeInUp}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white/5 backdrop-blur-lg p-8 rounded-2xl border border-white/10"
                >
                  <div className="text-5xl font-black text-[#F5A623] mb-4">{item.percentage}</div>
                  <h4 className="text-xl font-black mb-3">{item.title}</h4>
                  <p className="text-gray-300">{item.desc}</p>
                </motion.div>
              ))}
            </div>

            <motion.div {...fadeInUp} className="text-center">
              <div className="inline-block bg-gradient-to-r from-[#F5A623] to-[#FF9500] rounded-2xl p-8">
                <p className="text-xl text-[#1A1A1A] font-bold mb-2">Retorno proyectado:</p>
                <p className="text-4xl font-black text-[#1A1A1A]">4–6X en 36 meses</p>
                <p className="text-lg text-[#1A1A1A] mt-2">(sobre valuación pre-revenue)</p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ INVERSORES */}
        <section className="py-20 bg-[#FFF9ED]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...fadeInUp} className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-black text-[#1A1A1A] mb-4">FAQ Inversores</h2>
            </motion.div>

            <div className="space-y-4">
              {[
                {
                  q: '¿Cómo funciona la indexación?',
                  a: 'La indexación protege el valor del ahorro del usuario aplicando un 1% mensual sobre la meta definida. Esto compensa la devaluación y asegura que el poder adquisitivo del ahorro se mantenga durante el período de ahorro.'
                },
                {
                  q: '¿Qué diferencia a KOMPA de BNPL tradicional?',
                  a: 'A diferencia del BNPL tradicional que es "compra ahora, paga después" (generando deuda), KOMPA es SNBL: "ahorra ahora, compra después". El usuario construye capital primero, evitando endeudamiento y creando disciplina financiera.'
                },
                {
                  q: '¿Cuál es el marco regulatorio?',
                  a: 'KOMPA opera como plataforma de ahorro con propósito. Trabajamos con aliados financieros regulados para garantizar cumplimiento normativo. Nuestro roadmap incluye fortalecer el marco de compliance y gobierno corporativo.'
                },
                {
                  q: '¿Cómo manejan el riesgo de custodia?',
                  a: 'Los fondos se gestionan a través de alianzas con instituciones financieras establecidas. Implementamos trazabilidad completa y auditoría de todas las transacciones, garantizando transparencia total.'
                },
                {
                  q: '¿Cuál es la estrategia de expansión?',
                  a: 'Fase 1: Consolidación en Venezuela (100k usuarios). Fase 2: Expansión a otros mercados latinoamericanos con problemáticas similares. Fase 3: Diversificación de productos financieros (scoring, crédito puente).'
                }
              ].map((faq, idx) => (
                <motion.div
                  key={idx}
                  {...fadeInUp}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFAQ(openFAQ === idx ? null : idx)}
                    className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-lg font-black text-[#1A1A1A] pr-4">{faq.q}</span>
                    <ChevronDown
                      size={24}
                      className={`text-[#F5A623] flex-shrink-0 transition-transform ${openFAQ === idx ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {openFAQ === idx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-8 pb-6"
                    >
                      <p className="text-gray-600 leading-relaxed">{faq.a}</p>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA FINAL */}
        <section id="email-form" className="py-20 bg-gradient-to-r from-[#F5A623] to-[#FF9500]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div {...fadeInUp}>
              <h2 className="text-4xl sm:text-5xl font-black text-[#1A1A1A] mb-4">
                ¿Listo para ser parte del cambio?
              </h2>
              <p className="text-xl text-[#1A1A1A] mb-12">
                Únete a la ronda que está transformando las finanzas personales en Venezuela.
              </p>

              {!emailSent ? (
                <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto mb-8">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="tu@email.com"
                      required
                      className="flex-1 px-6 py-4 rounded-xl text-[#1A1A1A] focus:outline-none focus:ring-4 focus:ring-white/50"
                    />
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-8 py-4 bg-[#1A1A1A] text-white font-black rounded-xl hover:bg-[#2A2A2A] transition-all duration-300 flex items-center justify-center gap-2"
                    >
                      Enviar
                      <Mail size={20} />
                    </motion.button>
                  </div>
                  <p className="text-sm text-[#1A1A1A] mt-4">*Te contactaremos para agendar una reunión</p>
                </form>
              ) : (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="bg-white rounded-2xl p-8 max-w-md mx-auto mb-8"
                >
                  <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
                  <p className="text-xl font-black text-[#1A1A1A]">¡Gracias! Nos pondremos en contacto pronto.</p>
                </motion.div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 bg-[#1A1A1A] text-white font-black rounded-xl hover:bg-[#2A2A2A] transition-all duration-300 flex items-center justify-center gap-2 text-lg"
                  onClick={() => window.alert('Funcionalidad de Calendly pendiente de configuración')}
                >
                  Agendar reunión
                  <Calendar size={20} />
                </motion.button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Investors;
